import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
interface StreakMilestoneBurstProps {
  /** Milestone that was just reached (3, 7, 14, 30, ...) */
  milestone: number;
  /** Called when the burst animation has fully finished */
  onComplete?: () => void;
  /** Extra className for the absolute-positioned container */
  className?: string;
}
/**
 * One-shot celebration that fires when a streak milestone is crossed.
 *
 * Placed absolutely on top of the StreakFlame. Renders a radial burst of rays
 * + a small "Nieuw record!"-style label, both auto-dismissing.
 *
 * Part of App-Feel 2027 §3.4 — Levende sidebar streak feedback. The burst is
 * stateless: parent decides WHEN to render it by remounting via a `key` prop.
 *
 * Respects prefers-reduced-motion via useMotionBudget — falls back to a single
 * static label so the milestone is still acknowledged.
 */
export function StreakMilestoneBurst({
  milestone,
  onComplete,
  className = ''
}: StreakMilestoneBurstProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const duration = shouldAnimate ? 2200 : 1200;
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, duration);
    return () => clearTimeout(timer);
  }, [shouldAnimate, onComplete]);
  // Tier-based color + label — louder for higher milestones
  const tier = milestone >= 30 ? {
    ringColor: '#fde047',
    glowColor: '#7e22ce',
    label: 'Legendarisch!',
    rayCount: 12
  } : milestone >= 14 ? {
    ringColor: '#ffffff',
    glowColor: '#b91c1c',
    label: 'Onstuitbaar!',
    rayCount: 10
  } : milestone >= 7 ? {
    ringColor: '#fef08a',
    glowColor: '#dc2626',
    label: 'Heet!',
    rayCount: 8
  } : {
    ringColor: '#fdba74',
    glowColor: '#ea580c',
    label: 'Lekker bezig!',
    rayCount: 6
  };
  const rays = Array.from({
    length: tier.rayCount
  }, (_, i) => {
    const angle = i * 360 / tier.rayCount;
    return {
      angle,
      key: i
    };
  });
  return <AnimatePresence data-id="element-2599" data-mp="1ghsx5nkldmwm">
      {isVisible && <div className={`absolute inset-0 pointer-events-none z-30 flex items-center justify-center ${className}`} data-id="element-2600" data-mp="1gh00jffckds7">
          {/* Radial rays */}
          {shouldAnimate && rays.map(({
        angle,
        key
      }) => <motion.div key={key} className="absolute top-1/2 left-1/2" style={{
        width: 2,
        height: 22,
        background: `linear-gradient(to top, transparent, ${tier.ringColor})`,
        transformOrigin: 'bottom center',
        rotate: `${angle}deg`
      }} initial={{
        opacity: 0,
        scaleY: 0,
        x: -1,
        y: -22
      }} animate={{
        opacity: [0, 1, 0],
        scaleY: [0, 1, 1.4],
        y: [-22, -34, -42]
      }} transition={{
        duration: 0.9,
        ease: 'easeOut',
        delay: key % 4 * 0.04
      }} data-id="element-2601" data-mp="1v6fnekbrbz2m" />)}

          {/* Soft glow ring */}
          {shouldAnimate && <motion.div className="absolute rounded-full" style={{
        width: 60,
        height: 60,
        boxShadow: `0 0 16px 6px ${tier.glowColor}`,
        opacity: 0.5
      }} initial={{
        scale: 0.4,
        opacity: 0
      }} animate={{
        scale: [0.4, 1.4, 1.6],
        opacity: [0, 0.7, 0]
      }} transition={{
        duration: 1.1,
        ease: 'easeOut'
      }} data-id="element-2602" data-mp="2gde15jvtqkk" />}

          {/* Floating milestone label — anchored below the flame */}
          <motion.div className="absolute -bottom-7 whitespace-nowrap" initial={{
        opacity: 0,
        y: -4,
        scale: 0.7
      }} animate={shouldAnimate ? {
        opacity: [0, 1, 1, 0],
        y: [-4, 2, 2, -2],
        scale: [0.7, 1.05, 1, 0.95]
      } : {
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: shouldAnimate ? 2.0 : 0.2,
        times: shouldAnimate ? [0, 0.2, 0.8, 1] : undefined,
        ease: 'easeOut'
      }} data-id="element-2603" data-mp="5rg0z7y2irok">
            <div className="px-2 py-0.5 rounded-full text-[10px] font-black text-white shadow-md flex items-center gap-1" style={{
          background: tier.glowColor
        }} data-id="element-2604" data-mp="1mj1n3muthwk5">
              <span data-id="element-2605" data-mp="1vc4xfma61tak">🔥</span>
              <span data-id="element-2606" data-mp="1vbvf8kvd4qff">{milestone}</span>
              <span className="opacity-90" data-id="element-2607" data-mp="17l97yvt6na6h">·</span>
              <span data-id="element-2608" data-mp="17m1365ivb5w8">{tier.label}</span>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
/** Milestones at which we celebrate the streak. Crossing these fires a burst. */
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100] as const;
/**
 * Given a previous and a current streak value, returns the milestone that
 * was just crossed (one of STREAK_MILESTONES) or null if none.
 *
 * Pure utility — parents call this from a useEffect with refs to detect
 * transitions and remount StreakMilestoneBurst.
 */
export function getCrossedMilestone(prevStreak: number, currentStreak: number): number | null {
  if (currentStreak <= prevStreak) return null;
  for (const milestone of STREAK_MILESTONES) {
    if (prevStreak < milestone && currentStreak >= milestone) {
      return milestone;
    }
  }
  return null;
}